import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { buildQueryParams } from '../utils/workspaceQuery';

function EntityWorkspace({
  title,
  subtitle,
  breadcrumbs = [],
  entityType,
  apiPath,
  responseKey,
  columns,
  defaultFilters = {},
  filterFields = [],
  headerActions,
  onRowClick,
  rowActions,
  rowIdField = 'id',
  selectable = false,
  bulkActions = [],
  emptyMessage = 'No results match your filters.',
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState(defaultFilters);
  const [pagination, setPagination] = useState({ page: 1, perPage: 25, total: 0, pages: 1 });
  const [selected, setSelected] = useState(new Set());
  const [savedFilters, setSavedFilters] = useState([]);
  const [meta, setMeta] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const activeChips = useMemo(() => {
    const chips = [];
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && value !== null && value !== undefined) {
        const field = filterFields.find((item) => item.key === key);
        chips.push({
          key,
          label: field?.label || key,
          value: field?.optionLabel?.(value, meta) || value,
        });
      }
    });
    if (search.trim()) {
      chips.push({ key: '__q', label: 'Search', value: search.trim() });
    }
    return chips;
  }, [filters, filterFields, meta, search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildQueryParams(filters, pagination, search);
      const [listRes, savedRes, metaRes] = await Promise.all([
        api.get(`${apiPath}?${params.toString()}`),
        api.get(`/hdp/saved-filters?entity_type=${entityType}`).catch(() => ({ data: { saved_filters: [] } })),
        api.get('/hdp/meta').catch(() => ({ data: {} })),
      ]);
      setRows((listRes.data[responseKey] || []).map((item) => ({
        raw: item,
        id: item[rowIdField] || item.id,
      })));
      setPagination((prev) => ({
        ...prev,
        total: listRes.data.total ?? (listRes.data[responseKey] || []).length,
        pages: listRes.data.pages ?? 1,
      }));
      setSavedFilters(savedRes.data.saved_filters || []);
      setMeta(metaRes.data);
    } catch (err) {
      console.error(err);
      setRows([]);
      if (err.response?.status >= 400) {
        window.alert(`Failed to load ${title}: ${err.response?.data?.error || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [apiPath, entityType, filters, pagination.page, pagination.perPage, responseKey, rowIdField, search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const removeChip = (key) => {
    if (key === '__q') {
      setSearch('');
      setSearchInput('');
      setPagination((prev) => ({ ...prev, page: 1 }));
      return;
    }
    setFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const applyFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const runSearch = (event) => {
    event?.preventDefault();
    setSearch(searchInput);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const saveCurrentFilter = async () => {
    const name = window.prompt('Name this filter view');
    if (!name?.trim()) return;
    const payload = {
      name: name.trim(),
      entity_type: entityType,
      filters: { ...filters, q: search || undefined },
    };
    await api.post('/hdp/saved-filters', payload);
    loadData();
  };

  const applySavedFilter = (saved) => {
    const nextFilters = { ...(saved.filters || {}) };
    const q = nextFilters.q;
    delete nextFilters.q;
    setFilters(nextFilters);
    setSearch(q || '');
    setSearchInput(q || '');
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === rows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(rows.map((row) => row.id)));
    }
  };

  const runBulkAction = async (action) => {
    if (!selected.size) return;
    setBulkBusy(true);
    try {
      await api.post(`${apiPath}/bulk`, {
        action: action.action,
        ids: [...selected],
        ...(action.payload || {}),
      });
      setSelected(new Set());
      loadData();
    } catch (err) {
      console.error(err);
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="main-body entity-workspace">
      {breadcrumbs.length > 0 && (
        <nav className="entity-breadcrumbs" aria-label="Breadcrumb">
          {breadcrumbs.map((crumb, index) => (
            <span key={`${crumb.label}-${index}`} className="entity-breadcrumb-item">
              {crumb.to ? <Link to={crumb.to}>{crumb.label}</Link> : <span>{crumb.label}</span>}
              {index < breadcrumbs.length - 1 && <span className="entity-breadcrumb-sep">›</span>}
            </span>
          ))}
        </nav>
      )}

      <div className="hdp-section-head">
        <div>
          <h1 className="page-title">{title}</h1>
          {subtitle && (
            <p className="entity-workspace-subtitle">{subtitle}</p>
          )}
        </div>
        <div className="entity-workspace-actions">
          {headerActions}
        </div>
      </div>

      <form className="entity-workspace-search" onSubmit={runSearch}>
        <input
          className="form-input"
          placeholder="Search these results..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <button type="submit" className="btn btn-secondary btn-sm">Search</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={saveCurrentFilter}>
          Save filters
        </button>
      </form>

      <div className="entity-workspace-toolbar">
        {filterFields.map((field) => (
          <select
            key={field.key}
            className="form-input"
            value={filters[field.key] || ''}
            onChange={(e) => applyFilter(field.key, e.target.value)}
          >
            <option value="">{field.placeholder || `All ${field.label}`}</option>
            {(field.options?.(meta) || field.staticOptions || []).map((option) => {
              const value = typeof option === 'string' ? option : option.value;
              const label = typeof option === 'string' ? option : option.label;
              return <option key={value} value={value}>{label}</option>;
            })}
          </select>
        ))}
      </div>

      {activeChips.length > 0 && (
        <div className="filter-chips">
          {activeChips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              className="filter-chip"
              onClick={() => removeChip(chip.key)}
              title="Remove filter"
            >
              <span className="filter-chip-label">{chip.label}:</span>
              <span className="filter-chip-value">{chip.value}</span>
              <span className="filter-chip-remove">×</span>
            </button>
          ))}
        </div>
      )}

      {savedFilters.length > 0 && (
        <div className="saved-filter-row">
          <span className="saved-filter-label">Saved:</span>
          {savedFilters.map((saved) => (
            <button
              key={saved.filter_id}
              type="button"
              className="saved-filter-btn"
              onClick={() => applySavedFilter(saved)}
            >
              {saved.name}
            </button>
          ))}
        </div>
      )}

      {selectable && selected.size > 0 && bulkActions.length > 0 && (
        <div className="bulk-action-bar">
          <span>{selected.size} selected</span>
          {bulkActions.map((action) => (
            <button
              key={action.action}
              type="button"
              className={`btn btn-sm ${action.className || 'btn-secondary'}`}
              disabled={bulkBusy}
              onClick={() => runBulkAction(action)}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <p className="entity-workspace-empty">Loading...</p>
          ) : rows.length === 0 ? (
            <p className="entity-workspace-empty">{emptyMessage}</p>
          ) : (
            <table className="data-table entity-table">
              <thead>
                <tr>
                  {selectable && (
                    <th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        checked={selected.size === rows.length && rows.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </th>
                  )}
                  {columns.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                  {rowActions && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className={onRowClick ? 'data-table-row-clickable' : ''}
                    onClick={onRowClick ? () => onRowClick(row.raw) : undefined}
                  >
                    {selectable && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(row.id)}
                          onChange={() => toggleSelect(row.id)}
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td key={col.key}>{col.render(row.raw)}</td>
                    ))}
                    {rowActions && (
                      <td className="data-table-actions" onClick={(e) => e.stopPropagation()}>
                        <div className="data-table-actions-inner">{rowActions(row.raw, loadData)}</div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="entity-workspace-pagination">
        <span>
          {pagination.total} result{pagination.total === 1 ? '' : 's'}
        </span>
        <div className="entity-workspace-pagination-controls">
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            disabled={pagination.page <= 1}
            onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
          >
            Previous
          </button>
          <span>Page {pagination.page} of {pagination.pages}</span>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            disabled={pagination.page >= pagination.pages}
            onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default EntityWorkspace;
